/**
 * Hooks de GENTE de la academia (F3-D) — mitad REST, mitad local-first, y la
 * frontera es exactamente la de `docs/DATA_ACCESS_POLICY.md`.
 *
 * **Por REST (plan 06 · E4):** el directorio, el expediente de un alumno, la
 * ficha de un profesor y los vínculos de representante de la academia. Son
 * datos de OTRA persona, con PII (cédula, teléfono, dirección) y un alcance que
 * depende del rol y de la sede — nada de eso cabe en un `with:` plano, y el
 * intento costó replicar 2 000 expedientes al móvil de cada administrador.
 *
 * **Local-first:** mis matrículas, mis certificados, mi propio expediente, mi
 * ficha y mis pupilos (`my_cohorts_student`, `my_certificates`,
 * `my_membership_profiles`) y mi rol en la org (`organizations`).
 *
 * **La escritura es toda del servidor.** Los `PUT` de abajo invalidan la caché
 * de lectura; no hay optimismo local porque no hay fila local que actualizar.
 */
import type { Accessor } from "solid-js";
import {
  academyPeopleQueryKeys,
  type DirectoryMemberRow,
  getMyGuardianWardsQuery,
  getMyStudentProfileQuery,
  getMyTeacherProfileQuery,
  getUserCertificatesQuery,
  getUserEnrollmentsQuery,
  type GuardianLinkRow,
  normalizeOrgRole,
  type OrgCapability,
  roleHasCapability,
  type StudentRecord,
  type StudentRecordPatch,
  type TeacherRecord,
  type TeacherRecordPatch,
} from "@space/shared";
import {
  createPowerSyncQuery,
  type CreatePowerSyncQueryResult,
  createPowerSyncSingle,
} from "@space/shared/solid";
import { useQueryClient } from "@tanstack/solid-query";
import {
  and,
  eq,
  type GuardianLink,
  member,
  type StudentProfile,
  type TeacherProfile,
} from "@space/powersync";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

function useBase() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { userId } = useSession();
  return { db, drizzle, userId, staleTime: OFFLINE_CACHE_STALE_TIME_MS };
}

/**
 * Directorio de una organización (una fila por miembro + expediente resumido),
 * **sin identidad**: nombre/correo/imagen los aporta `useOrgIdentities` y se
 * pegan con `hydrateDirectory` (D3 · plan 06, E2).
 *
 * **Camino único desde E4.** Lo sirve `GET /api/academy/:orgId/directory`, que
 * exige gestionar alumnos o miembros y acota por sede al `campus_admin` — un
 * alcance que las reglas de sync no expresan (`org_admin_scope` replicaba los
 * expedientes de la academia ENTERA a cualquier admin). La rama local y su flag
 * de rollback murieron con el recorte de `student_profile`: sin esas filas
 * replicadas, la consulta Drizzle devolvía vacío para todo el mundo, así que
 * conservarla habría sido un interruptor que solo apaga la pantalla.
 *
 * El endpoint NO devuelve identidad a propósito: si la devolviera habría un
 * segundo sitio decidiendo quién ve un correo, que es el defecto D3.
 */
export function useOrgDirectory(
  orgId: Accessor<string | undefined>,
): CreatePowerSyncQueryResult<DirectoryMemberRow> {
  // `Error` y no `unknown`: la firma pública del hook expone `error: Error | null`
  // (la que tenía con PowerSync), y `handleApiResponse` ya lanza `Error`.
  const rest = createApiQuery<DirectoryMemberRow[], Error>(
    () => academyPeopleQueryKeys.directory(orgId()),
    async () =>
      api.GET("/api/academy/{orgId}/directory", {
        params: { path: { orgId: orgId() ?? "" } },
      }),
    () => ({ enabled: Boolean(orgId()), staleTime: OFFLINE_CACHE_STALE_TIME_MS }),
  );
  // Se conserva la forma `CreatePowerSyncQueryResult` para que las pantallas no
  // se enteren del cambio de transporte (misma firma que tenían con el espejo).
  return {
    get data() {
      return rest.data;
    },
    get isFetching() {
      return rest.isFetching;
    },
    get isLoading() {
      return rest.isLoading;
    },
    get isPending() {
      return rest.isPending;
    },
    get error() {
      return rest.error;
    },
    get isError() {
      return rest.isError;
    },
    get status() {
      return rest.status;
    },
    refetch: () => {
      void rest.refetch();
    },
  };
}

/**
 * Expediente de un alumno en la org (`GET /api/academy/:orgId/students/:userId`).
 *
 * 404 = «no hay expediente **que tú puedas ver**»: el servidor no distingue
 * entre inexistente y fuera de tu sede a propósito, para no confirmar que un
 * alumno de otra sede existe. La pantalla lo trata como «sin expediente» y
 * ofrece crearlo, igual que hacía con el `limit(1)` vacío del espejo local.
 */
export function useStudentRecord(
  orgId: Accessor<string | undefined>,
  userId: Accessor<string | undefined>,
) {
  return createApiQuery<StudentRecord, unknown>(
    () => academyPeopleQueryKeys.studentProfile(orgId(), userId()),
    async () =>
      api.GET("/api/academy/{orgId}/students/{userId}", {
        params: { path: { orgId: orgId() ?? "", userId: userId() ?? "" } },
      }),
    () => ({
      enabled: Boolean(orgId()) && Boolean(userId()),
      staleTime: OFFLINE_CACHE_STALE_TIME_MS,
      // Sin expediente aún NO es un fallo transitorio: reintentar solo retrasa
      // el formulario de alta que la pantalla quiere enseñar.
      retry: false,
    }),
  );
}

/** Guarda (crea o actualiza) el expediente. Patch DISPERSO: lo ausente no se toca. */
export function useSaveStudentRecord(
  orgId: Accessor<string | undefined>,
  userId: Accessor<string | undefined>,
) {
  const qc = useQueryClient();
  return createApiMutation<StudentRecordPatch, StudentRecord, unknown>(
    async (patch) =>
      api.PUT("/api/academy/{orgId}/students/{userId}", {
        params: { path: { orgId: orgId() ?? "", userId: userId() ?? "" } },
        body: patch,
      }),
    () => ({
      onSuccess: () => {
        void qc.invalidateQueries({
          queryKey: academyPeopleQueryKeys.studentProfile(orgId(), userId()),
        });
        // El directorio lleva cédula, estado y sede del expediente: si no se
        // invalida, la lista sigue enseñando el valor viejo hasta que caduque.
        void qc.invalidateQueries({ queryKey: academyPeopleQueryKeys.directory(orgId()) });
      },
    }),
  );
}

/** Ficha de profesor (`GET /api/academy/:orgId/teachers/:userId`). */
export function useTeacherRecord(
  orgId: Accessor<string | undefined>,
  userId: Accessor<string | undefined>,
) {
  return createApiQuery<TeacherRecord, unknown>(
    () => academyPeopleQueryKeys.teacherProfile(orgId(), userId()),
    async () =>
      api.GET("/api/academy/{orgId}/teachers/{userId}", {
        params: { path: { orgId: orgId() ?? "", userId: userId() ?? "" } },
      }),
    () => ({
      enabled: Boolean(orgId()) && Boolean(userId()),
      staleTime: OFFLINE_CACHE_STALE_TIME_MS,
      retry: false,
    }),
  );
}

/** Guarda (crea o actualiza) la ficha de profesor. */
export function useSaveTeacherRecord(
  orgId: Accessor<string | undefined>,
  userId: Accessor<string | undefined>,
) {
  const qc = useQueryClient();
  return createApiMutation<TeacherRecordPatch, TeacherRecord, unknown>(
    async (patch) =>
      api.PUT("/api/academy/{orgId}/teachers/{userId}", {
        params: { path: { orgId: orgId() ?? "", userId: userId() ?? "" } },
        body: patch,
      }),
    () => ({
      onSuccess: () => {
        void qc.invalidateQueries({
          queryKey: academyPeopleQueryKeys.teacherProfile(orgId(), userId()),
        });
        void qc.invalidateQueries({ queryKey: academyPeopleQueryKeys.directory(orgId()) });
      },
    }),
  );
}

// D3 (plan 06 · E2): `useMemberIdentity` se ha BORRADO junto con su consulta
// Drizzle. La identidad de una persona la sirve ahora `useOrgIdentities`
// (`./use-org-identities`), fuente única del cliente y el único sitio donde el
// correo puede llegar —o no— según lo que el servidor decida por capacidad.

/** Matrículas de un usuario (join con la cohorte). */
export function useUserEnrollments(userId: Accessor<string | undefined>) {
  const base = useBase();
  type Row = Awaited<ReturnType<ReturnType<typeof getUserEnrollmentsQuery>["execute"]>>[number];
  return createPowerSyncQuery<Row>({
    queryKey: () => academyPeopleQueryKeys.enrollments(userId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const uid = userId();
      return db && uid ? getUserEnrollmentsQuery(db, uid) : null;
    },
    enabled: () => Boolean(userId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** Certificados de un usuario (local; vacío si no están sincronizados). */
export function useUserCertificates(userId: Accessor<string | undefined>) {
  const base = useBase();
  type Row = Awaited<ReturnType<ReturnType<typeof getUserCertificatesQuery>["execute"]>>[number];
  return createPowerSyncQuery<Row>({
    queryKey: () => academyPeopleQueryKeys.certificates(userId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const uid = userId();
      return db && uid ? getUserCertificatesQuery(db, uid) : null;
    },
    enabled: () => Boolean(userId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/**
 * Vínculos de representante de la organización (para el expediente).
 *
 * E4: por REST (`GET /api/org/listGuardians`, que ya existía y ya era la puerta
 * de ESCRITURA — `addGuardian`/`removeGuardian`). La consulta local leía
 * `guardian_link` de `org_admin_scope`, es decir, todos los vínculos de la
 * academia; el endpoint devuelve los mismos datos ya resueltos con nombre y
 * correo, así que además desaparece un join de identidad.
 */
export function useOrgGuardianLinks(orgId: Accessor<string | undefined>) {
  return createApiQuery<GuardianLinkRow[], unknown>(
    () => academyPeopleQueryKeys.guardianLinks(orgId()),
    async () =>
      api.GET("/api/org/listGuardians", {
        params: { query: { organizationId: orgId() ?? "" } },
      }),
    () => ({ enabled: Boolean(orgId()), staleTime: OFFLINE_CACHE_STALE_TIME_MS }),
  );
}

// ─── auto-vista (alumno / representante) ─────────────────────────────────────

/** MI expediente de alumno. */
export function useMyStudentProfile() {
  const base = useBase();
  return createPowerSyncQuery<StudentProfile>({
    queryKey: () => academyPeopleQueryKeys.myStudentProfile(base.userId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const uid = base.userId();
      return db && uid ? getMyStudentProfileQuery(db, uid) : null;
    },
    enabled: () => Boolean(base.userId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** MI perfil de profesor. */
export function useMyTeacherProfile() {
  const base = useBase();
  return createPowerSyncSingle<TeacherProfile>({
    queryKey: () => academyPeopleQueryKeys.myTeacherProfile(base.userId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const uid = base.userId();
      return db && uid ? getMyTeacherProfileQuery(db, uid) : null;
    },
    enabled: () => Boolean(base.userId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** MIS pupilos como representante (guardian_link donde soy el guardian). */
export function useMyGuardianWards() {
  const base = useBase();
  return createPowerSyncQuery<GuardianLink>({
    queryKey: () => academyPeopleQueryKeys.myGuardianLinks(base.userId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const uid = base.userId();
      return db && uid ? getMyGuardianWardsQuery(db, uid) : null;
    },
    enabled: () => Boolean(base.userId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

// ─── capacidad del usuario en la org activa (gating de UI) ────────────────────

/**
 * Rol del usuario actual en `orgId` (de `member`, sincronizado por el stream
 * `organizations`). Devuelve helpers de capacidad para gatear la edición en la
 * UI. El write-path re-valida siempre: esto es solo cosmético.
 */
export function useMyOrgRole(orgId: Accessor<string | undefined>) {
  const base = useBase();
  type MemberRow = { role: string | null };
  const query = createPowerSyncSingle<MemberRow>({
    queryKey: () => academyPeopleQueryKeys.myRole(orgId(), base.userId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const oid = orgId();
      const uid = base.userId();
      return db && oid && uid
        ? db
            .select({ role: member.role })
            .from(member)
            .where(and(eq(member.organization_id, oid), eq(member.user_id, uid)))
            .limit(1)
        : null;
    },
    enabled: () => Boolean(orgId()) && Boolean(base.userId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });

  const role = () => normalizeOrgRole(query.data?.role);
  const can = (cap: OrgCapability) => roleHasCapability(query.data?.role, cap);
  return { role, can, isLoading: () => query.isLoading };
}
